import * as React from 'react';
import { Modal, Button } from 'react-bootstrap';

// YouTube id for the current demo video. When a NEW demo is recorded, update this
// id AND bump DEMO_VIDEO_VERSION in Navigation.tsx so the auto-popup re-appears
// for everyone the next time they log in.
export const DEMO_VIDEO_YOUTUBE_ID = 'Nld1KH6z4Bk';

// The chapter list, mirroring the one in the video's YouTube description. Keep the
// two in step: this is the copy a practitioner actually sees, since the popup plays
// the video in place and never sends them to YouTube to read the description.
//
// They are clickable rather than printed labels because the tour runs nearly an
// hour. A practitioner who came back for one thing (say Supplement Protocols) would
// otherwise have to scrub for it inside an embed with no chapter markers of its own.
type Chapter = { time: string; title: string };

export const DEMO_VIDEO_CHAPTERS: Chapter[] = [
  { time: '0:00', title: 'LabSmarts Product Tour' },
  { time: '1:04', title: 'Add Clients' },
  { time: '3:39', title: 'Add Test Results / Upload Lab PDFs' },
  { time: '4:43', title: 'Manual Entry and Auto Calculations' },
  { time: '8:13', title: 'Add Your Own Markers and Sections' },
  { time: '14:06', title: 'Manage My Markers & Sections' },
  { time: '18:18', title: 'Color-Coded Results Overview' },
  { time: '21:11', title: 'Pattern Insights and Likelihood Gauges' },
  { time: '23:58', title: 'Section Notes' },
  { time: '24:38', title: 'The Functional Blood Work Map™' },
  { time: '29:23', title: 'In-Section Supplement Recommendations' },
  { time: '33:40', title: 'Section Notes Summarized and Test Insights' },
  { time: '34:28', title: 'Systemic Formulas Supplement Protocols' },
  { time: '40:46', title: 'Build a Client Supplement List' },
  { time: '42:40', title: 'Customize Report Settings' },
  { time: '47:07', title: 'Markers Outside of Optimal' },
  { time: '48:20', title: 'Export Client Reports' },
  { time: '48:50', title: 'Compare Results and Track Progress' },
  { time: '50:35', title: 'Overall Client Progress & Trends' },
  { time: '51:42', title: 'Schedule a Clinical Review' },
  { time: '52:33', title: 'Export Client Data for AI' },
  { time: '53:46', title: 'Compare Tests on One Bar Graph' },
];

// 'mm:ss' or 'h:mm:ss' into the seconds YouTube's start param wants.
export function chapterSeconds(time: string): number {
  return time
    .split(':')
    .map(Number)
    .reduce((total, part) => total * 60 + part, 0);
}

type Props = {
  show: boolean;
  // True when this practitioner already dismissed an EARLIER version of the tour,
  // so they are being re-shown a re-recording rather than meeting it for the first
  // time. It only changes the copy above the video: a returning practitioner needs
  // to be told this one is updated and what it adds, and a new sign-up would find
  // "updated" meaningless. Navigation works it out from localStorage.
  returning?: boolean;
  onHide: () => void;
};

type State = {
  show: boolean;
  // Seconds to start the embed at, or undefined for the top of the video. Set by
  // clicking a chapter. Changing it re-mounts the iframe (see the key below), which
  // is the only way to seek an embed without pulling in the YouTube iframe API.
  startSeconds?: number;
};

export class DemoVideoModal extends React.Component<Props, State> {
  state: State = {
    show: this.props.show
  };

  componentWillReceiveProps(nextProps: Props) {
    this.setState({
      show: nextProps.show,
      // A re-open starts at the top again. Reopening mid-chapter would be a
      // surprise for the practitioner who closed it and came back later.
      startSeconds: nextProps.show && !this.state.show ? undefined : this.state.startSeconds,
    });
  }

  // The player, so a chapter click can bring it back into view.
  videoRef = React.createRef<HTMLDivElement>();

  handleClose = () => {
    this.props.onHide();
  }

  playChapter = (time: string) => {
    this.setState({ startSeconds: chapterSeconds(time) }, () => {
      // The chapter list sits below the player, so whoever scrolled down to read
      // it would otherwise seek the video and still be looking at the list. Scroll
      // the modal's own body, not the window: with `scrollable` set, that is the
      // element Bootstrap gives the overflow to.
      const body = this.videoRef.current?.closest('.modal-body');
      if (!body) {
        return;
      }
      if (typeof body.scrollTo === 'function') {
        body.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        // Older browsers take no options object, and neither does jsdom.
        body.scrollTop = 0;
      }
    });
  };

  renderChapters() {
    return (
      <div style={{ marginTop: 16 }}>
        <div
          style={{
            fontSize: 12,
            fontWeight: 600,
            letterSpacing: 0.4,
            textTransform: 'uppercase',
            color: '#766d66',
            marginBottom: 6,
          }}
        >
          Chapters
        </div>
        {/* Three balanced columns so seventeen chapters read as a short list rather
            than a scroll. They wrap to one column on a narrow window. */}
        <div
          style={{
            columnCount: 3,
            columnGap: 24,
            columnFill: 'balance',
            fontSize: 13,
            lineHeight: 1.55,
          }}
        >
          {DEMO_VIDEO_CHAPTERS.map(chapter => (
            <div
              key={chapter.time}
              style={{ breakInside: 'avoid', display: 'flex', gap: 8, marginBottom: 2 }}
            >
              <button
                type="button"
                onClick={() => this.playChapter(chapter.time)}
                title={`Play from ${chapter.time}`}
                style={{
                  background: 'none',
                  border: 0,
                  padding: 0,
                  color: '#337ab7',
                  fontVariantNumeric: 'tabular-nums',
                  cursor: 'pointer',
                  flex: '0 0 auto',
                }}
              >
                {chapter.time}
              </button>
              <button
                type="button"
                onClick={() => this.playChapter(chapter.time)}
                style={{
                  background: 'none',
                  border: 0,
                  padding: 0,
                  color: '#333',
                  textAlign: 'left',
                  cursor: 'pointer',
                }}
              >
                {chapter.title}
              </button>
            </div>
          ))}
        </div>
      </div>
    );
  }

  render() {
    if (!this.state.show) {
      return null;
    }

    const { returning } = this.props;
    const { startSeconds } = this.state;
    // autoplay only once a chapter has been picked. An embed that starts talking
    // the moment the popup appears on login is the wrong first impression.
    const src = startSeconds === undefined
      ? `https://www.youtube.com/embed/${DEMO_VIDEO_YOUTUBE_ID}`
      : `https://www.youtube.com/embed/${DEMO_VIDEO_YOUTUBE_ID}?start=${startSeconds}&autoplay=1`;

    return (
      <Modal show={this.state.show} onHide={this.handleClose} size="lg" scrollable>
        <Modal.Header closeButton style={{ backgroundColor: '#337ab7', color: '#fff' }}>
          <Modal.Title>
            {returning
              ? 'Updated Demo: See What’s New in LabSmarts'
              : 'Welcome to LabSmarts'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {returning ? (
            <p>
              The full tour has been re-recorded, and this version covers everything
              that shipped recently: the <b>Functional Blood Work Map&trade;</b> results
              overview, the new <b>Pattern Insights</b> panels and likelihood gauges,
              <b> Supplement Protocols</b> and the client supplement list, and
              <b> My Markers &amp; Sections</b> for markers LabSmarts doesn&rsquo;t carry
              yet. You can watch this again any time from the <b>Help</b> menu.
            </p>
          ) : (
            <p>
              Take the full tour of what LabSmarts can do, from entering a client&rsquo;s
              lab results through to the finished client report. You can watch this again
              any time from the <b>Help</b> menu.
            </p>
          )}
          {/* Responsive 16:9 embed */}
          <div ref={this.videoRef} style={{ position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden' }}>
            <iframe
              // Re-mounts on every chapter click; without it React reuses the
              // element and the new src never loads.
              key={startSeconds === undefined ? 'start' : startSeconds}
              title="LabSmarts Demo Video"
              src={src}
              style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0 }}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
          {this.renderChapters()}
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={this.handleClose}>Close</Button>
        </Modal.Footer>
      </Modal>
    );
  }
}
